import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/loading/Loading';
import img from '../assets/contact/touch.png';
import { getContactFormHandlerUrl } from '../utils/contactFormApi';

// Hook to track window width
function useWindowWidth() {
    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handle = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handle);
        return () => window.removeEventListener('resize', handle);
    }, []);
    return width;
}

const inputBase = {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    background: '#162040',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    color: '#fff',
    padding: '10px 14px',
    fontSize: '14px',
    outline: 'none',
};
const inputErr  = { ...inputBase, border: '1px solid #e05c5c' };
const errStyle  = { color: '#e07575', fontSize: '12px', marginTop: '4px' };
const labelStyle = { display: 'block', color: '#fff', fontSize: '13.5px', fontWeight: 500, marginBottom: '6px' };

export default function ContactModal({ isOpen, onClose }) {
    const navigate   = useNavigate();
    const width      = useWindowWidth();
    const isMobile   = width < 640;

    const [formData, setFormData] = useState({
        firstName: '', lastName: '', email: '', industry: '', phone: '', message: ''
    });
    const [formErrors, setFormErrors]     = useState({});
    const [showAlert, setShowAlert]       = useState(false);
    const [showErrorAlert, setErrorShowAlert] = useState(false);
    const [loading, setLoading]           = useState(false);

    const industries = [
        'Art', 'Retail Stores', 'Real Estate', 'Education', 'E-Commerce', 'Events',
        'Architecture', 'Health Care', 'Hospitality', 'Marketing', 'Manufacturers', 'Other'
    ];

    if (!isOpen) return null;

    const validateForm = () => {
        let errors = {};
        if (!formData.firstName.trim()) errors.firstName = 'First name is required';
        if (!formData.lastName.trim())  errors.lastName  = 'Last name is required';
        if (!formData.email.trim())     errors.email     = 'Email is required';
        if (!formData.industry.trim())  errors.industry  = 'Industry is required';
        if (!formData.phone.trim())     errors.phone     = 'Phone number is required';
        if (!formData.message.trim())   errors.message   = 'Message is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async e => {
        const contactFormHandlerUrl = getContactFormHandlerUrl();
        e.preventDefault();
        try {
            setLoading(true);
            if (!validateForm()) { setLoading(false); return; }
            setShowAlert(false);
            setErrorShowAlert(false);
            const response = await fetch(contactFormHandlerUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            if (response.ok) {
                setFormData({ firstName: '', lastName: '', email: '', industry: '', phone: '', message: '' });
                setLoading(false);
                onClose();
                navigate('/thank-you');
            } else {
                setLoading(false);
                setErrorShowAlert(true);
            }
        } catch (error) {
            setLoading(false);
            setErrorShowAlert(true);
        }
    };

    // ── SVG icons ──────────────────────────────────────────────────────────
    const IconLocation = (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 7C10 7.53043 9.78929 8.03914 9.41421 8.41421C9.03914 8.78929 8.53043 9 8 9C7.46957 9 6.96086 8.78929 6.58579 8.41421C6.21071 8.03914 6 7.53043 6 7C6 6.46957 6.21071 5.96086 6.58579 5.58579C6.96086 5.21071 7.46957 5 8 5C8.53043 5 9.03914 5.21071 9.41421 5.58579C9.78929 5.96086 10 6.46957 10 7Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M13 7C13 11.7613 8 14.5 8 14.5C8 14.5 3 11.7613 3 7C3 5.67392 3.52678 4.40215 4.46447 3.46447C5.40215 2.52678 6.67392 2 8 2C9.32608 2 10.5979 2.52678 11.5355 3.46447C12.4732 4.40215 13 5.67392 13 7Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
    const IconPhone = (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <g clipPath="url(#cp1)">
                <path d="M1.5 4.5C1.5 10.0227 5.97733 14.5 11.5 14.5H13C13.3978 14.5 13.7794 14.342 14.0607 14.0607C14.342 13.7794 14.5 13.3978 14.5 13V12.0853C14.5 11.7413 14.266 11.4413 13.932 11.358L10.9833 10.6207C10.69 10.5473 10.382 10.6573 10.2013 10.8987L9.55467 11.7607C9.36667 12.0113 9.042 12.122 8.748 12.014C7.65659 11.6128 6.66544 10.9791 5.84319 10.1568C5.02094 9.33456 4.38725 8.34341 3.986 7.252C3.878 6.958 3.98867 6.63333 4.23933 6.44533L5.10133 5.79867C5.34333 5.618 5.45267 5.30933 5.37933 5.01667L4.642 2.068C4.55111 1.71923 4.23533 1.5 3.91467 1.5H3C2.60218 1.5 2.22064 1.65804 1.93934 1.93934C1.65804 2.22064 1.5 2.60218 1.5 3V4.5Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </g>
            <defs><clipPath id="cp1"><rect width="16" height="16" fill="white"/></clipPath></defs>
        </svg>
    );
    const IconEmail = (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M14.5 4.5V11.5C14.5 11.8978 14.342 12.2794 14.0607 12.5607C13.7794 12.842 13.3978 13 13 13H3C2.60218 13 2.22064 12.842 1.93934 12.5607C1.65804 12.2794 1.5 11.8978 1.5 11.5V4.5M14.5 4.5C14.5 4.10218 14.342 3.72064 14.0607 3.43934C13.7794 3.15804 13.3978 3 13 3H3C2.60218 3 2.22064 3.15804 1.93934 3.43934C1.65804 3.72064 1.5 4.10218 1.5 4.5M14.5 4.5V4.662C14.5 4.9181 14.4345 5.16994 14.3096 5.39353C14.1848 5.61712 14.0047 5.80502 13.7867 5.93933L8.78667 9.016C8.55014 9.16169 8.2778 9.23883 8 9.23883C7.7222 9.23883 7.44986 9.16169 7.21333 9.016L2.21333 5.94C1.99528 5.80569 1.81525 5.61779 1.69038 5.3942C1.56551 5.1706 1.49997 4.91876 1.5 4.66267V4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );

    const contactItems = [
        { icon: IconLocation, content: 'Sheikh Zayed Road, Al Qouz First, Dubai UAE' },
        { icon: IconPhone,    content: <a href="tel:+971524955029" style={{ color: 'rgba(255,255,255,0.75)', textDecoration: 'none' }}>+971 52 495 5029</a> },
        { icon: IconEmail,    content: <a href="mailto:info@avrlabs.io" style={{ color: 'rgba(255,255,255,0.75)', textDecoration: 'none' }}>info@avrlabs.io</a> },
    ];

    // ── Responsive layout values ────────────────────────────────────────────
    const modalStyle = {
        width: '100%',
        maxWidth: '900px',
        maxHeight: '92vh',
        overflowY: 'auto',
        borderRadius: '16px',
        background: '#0e1c3d',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        position: 'relative',
    };

    const leftPanelStyle = {
        width: isMobile ? '100%' : '260px',
        minWidth: isMobile ? 'unset' : '260px',
        background: '#0a1530',
        borderRadius: isMobile ? '16px 16px 0 0' : '16px 0 0 16px',
        padding: isMobile ? '24px 20px' : '36px 28px',
        display: 'flex',
        flexDirection: isMobile ? 'row' : 'column',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        alignItems: isMobile ? 'flex-start' : 'stretch',
        gap: isMobile ? '0' : '0',
        borderRight: isMobile ? 'none' : '1px solid rgba(255,255,255,0.06)',
        borderBottom: isMobile ? '1px solid rgba(255,255,255,0.06)' : 'none',
    };

    const rightPanelStyle = {
        flex: 1,
        minWidth: 0,
        padding: isMobile ? '24px 20px' : '36px 36px 36px 32px',
        position: 'relative',
    };

    // On mobile: pair fields stack vertically instead of side by side
    const fieldRowStyle = {
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '0' : '16px',
        marginBottom: '16px',
    };

    const fieldColStyle = {
        flex: 1,
        minWidth: 0,
        marginBottom: isMobile ? '16px' : '0',
    };

    return (
        <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(6px)',
            zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: isMobile ? '12px' : '20px',
            overflowY: 'auto',
        }}>
            <div style={modalStyle}>
                {loading && <LoadingSpinner />}

                {/* ── Left Panel ── */}
                <div style={leftPanelStyle}>
                    {/* Title + divider — always full width on mobile */}
                    <div style={{ width: '100%' }}>
                        <h5 style={{ color: '#fff', fontSize: '18px', fontWeight: 700, margin: '0 0 10px' }}>
                            Contact information
                        </h5>
                        <div style={{ width: '40px', height: '2px', background: '#3b7fe8', marginBottom: isMobile ? '16px' : '28px' }} />
                    </div>

                    {/* Contact items */}
                    <ul style={{
                        listStyle: 'none', padding: 0,
                        margin: 0,
                        flex: isMobile ? '1 1 auto' : undefined,
                        width: isMobile ? '100%' : undefined,
                    }}>
                        {contactItems.map((item, i, arr) => (
                            <li key={i} style={{
                                display: 'flex', alignItems: 'flex-start', gap: '10px',
                                color: 'rgba(255,255,255,0.75)', fontSize: '13.5px', lineHeight: '1.5',
                                borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                                paddingBottom: '14px', marginBottom: '14px',
                            }}>
                                <span style={{ flexShrink: 0, marginTop: '2px' }}>{item.icon}</span>
                                <span>{item.content}</span>
                            </li>
                        ))}
                    </ul>

                    {/* Arch image — hide on mobile to save space */}
                    {!isMobile && (
                        <img src={img} alt="AVRLabs" style={{
                            width: '100%', height: 'auto', display: 'block',
                            opacity: 0.85, marginTop: '16px',
                        }} />
                    )}
                </div>

                {/* ── Right Panel ── */}
                <div style={rightPanelStyle}>

                    {/* Close button */}
                    <button onClick={onClose} style={{
                        position: 'absolute', top: '14px', right: '16px',
                        background: 'transparent', border: 'none',
                        color: 'rgba(255,255,255,0.5)', fontSize: '26px',
                        cursor: 'pointer', lineHeight: 1,
                    }}>&times;</button>

                    {showAlert && (
                        <div style={{ padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '16px', background: 'rgba(40,167,69,0.2)', color: '#6fcf97', border: '1px solid rgba(40,167,69,0.3)' }}>
                            Email sent successfully!
                        </div>
                    )}
                    {showErrorAlert && (
                        <div style={{ padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '16px', background: 'rgba(220,53,69,0.2)', color: '#eb5757', border: '1px solid rgba(220,53,69,0.3)' }}>
                            Failed to send email. Please try again later.
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>

                        {/* First / Last name */}
                        <div style={fieldRowStyle}>
                            <div style={fieldColStyle}>
                                <label style={labelStyle}>First name</label>
                                <input type="text" name="firstName" value={formData.firstName} onChange={handleChange}
                                    style={formErrors.firstName ? inputErr : inputBase} />
                                {formErrors.firstName && <div style={errStyle}>{formErrors.firstName}</div>}
                            </div>
                            <div style={fieldColStyle}>
                                <label style={labelStyle}>Last name</label>
                                <input type="text" name="lastName" value={formData.lastName} onChange={handleChange}
                                    style={formErrors.lastName ? inputErr : inputBase} />
                                {formErrors.lastName && <div style={errStyle}>{formErrors.lastName}</div>}
                            </div>
                        </div>

                        {/* Email / Phone */}
                        <div style={fieldRowStyle}>
                            <div style={fieldColStyle}>
                                <label style={labelStyle}>Email</label>
                                <input type="email" name="email" value={formData.email} onChange={handleChange}
                                    style={formErrors.email ? inputErr : inputBase} />
                                {formErrors.email && <div style={errStyle}>{formErrors.email}</div>}
                            </div>
                            <div style={fieldColStyle}>
                                <label style={labelStyle}>Phone</label>
                                <input type="number" name="phone" value={formData.phone} onChange={handleChange}
                                    style={formErrors.phone ? inputErr : inputBase} />
                                {formErrors.phone && <div style={errStyle}>{formErrors.phone}</div>}
                            </div>
                        </div>

                        {/* Industry */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Industry</label>
                            <select name="industry" value={formData.industry} onChange={handleChange}
                                style={formErrors.industry ? inputErr : inputBase}>
                                <option value="">Select Industry</option>
                                {industries.map((ind, i) => <option key={i} value={ind}>{ind}</option>)}
                            </select>
                            {formErrors.industry && <div style={errStyle}>{formErrors.industry}</div>}
                        </div>

                        {/* Message */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={labelStyle}>Message</label>
                            <textarea name="message" rows="5" value={formData.message} onChange={handleChange}
                                style={{ ...(formErrors.message ? inputErr : inputBase), resize: 'vertical' }} />
                            {formErrors.message && <div style={errStyle}>{formErrors.message}</div>}
                        </div>

                        {/* Submit */}
                        <button type="submit" disabled={loading} style={{
                            width: isMobile ? '100%' : 'auto',
                            padding: '12px 36px',
                            background: 'linear-gradient(90deg, #3b7fe8, #2dc7f0)',
                            color: '#fff', border: 'none', borderRadius: '6px',
                            fontSize: '14px', fontWeight: 600,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.5 : 1,
                            boxSizing: 'border-box',
                        }}>
                            Send Now
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}